import { Injectable } from '@angular/core';
import {
	CachePolicy,
	CachePolicyEntity,
	camelToSnake,
	UrlService,
} from '@datachain/ui-sdk/common';
import { Observable } from 'rxjs';

import { DcBaseRequester } from '../core/dc-base.requester';
import { ITimeline, ITimelineResponse } from './timeline.model';

@Injectable({
	providedIn: 'root',
})
export class TimelineRequester extends DcBaseRequester<ITimelineResponse> {
	protected resource: string;
	protected elementsListUrl: string;
	protected singleElementUrl: string;

	public constructor(private readonly urlService: UrlService) {
		super();
		this.resource = this.generateResourceUrl('TIMELINES_GET_ALL');
		this.elementsListUrl = this.resource;
		this.singleElementUrl = this.resource;
	}

	public getTimelineList(
		includePublic: boolean,
		useCache = true
	): Observable<ReadonlyArray<ITimelineResponse>> {
		const resourceUrl = this.generateResourceUrl('TIMELINES_GET_ALL_DETAILED');

		const url = this.urlService.generateUrl(
			resourceUrl,
			{},
			{
				project: this.projectId,
				excludePublic: !includePublic,
			}
		);
		const cachePolicy = useCache
			? CachePolicyEntity.build({
					policy: CachePolicy.NetworkAndCache,
					evictTimeoutInSeconds: 60,
					isCacheable: true,
			  })
			: CachePolicyEntity.build({
					policy: CachePolicy.NetworkOnly,
					evictTimeoutInSeconds: 0,
					isCacheable: false,
			  });
		return this.get<Array<ITimelineResponse>>(url, cachePolicy);
	}

	public getTimeline(timelineId: number): Observable<ITimeline> {
		const resourceUrl = this.generateResourceUrl('TIMELINES_GET');
		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				id: timelineId,
			},
			{
				project: this.projectId,
			}
		);
		return this.http.get<ITimeline>(url);
	}

	public changeActivationStatusTimeline(timeline: ITimeline): Observable<void> {
		const resourceUrl = this.generateResourceUrl('TIMELINES_EDIT');
		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				id: timeline.id,
			},
			{
				project: this.projectId,
			}
		);
		return this.http.put<void>(url, {
			metadata: {
				active: timeline.metadata.actif,
				[camelToSnake('shortLabel')]: timeline.metadata.label,
			},
		});
	}

	public deleteTimeline(timelineId: number): Observable<void> {
		const resourceUrl = this.generateResourceUrl('TIMELINES_DELETE');
		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				id: timelineId,
			},
			{
				project: this.projectId,
			}
		);
		return this.http.delete<void>(url);
	}

	public deleteManyTimelines(timelineIds: Array<number>): Observable<void> {
		const resourceUrl = this.generateResourceUrl('TIMELINES_DELETE_BULK');

		const url = this.urlService.generateUrl(
			resourceUrl,
			{},
			{
				project: this.projectId,
				timeLinesId: timelineIds,
			}
		);
		return this.http.delete<void>(url);
	}

	public duplicateTimeline(timelineId: number): Observable<number> {
		const resourceUrl = this.generateResourceUrl('TIMELINES_DUPLICATE');
		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				id: timelineId,
			},
			{
				project: this.projectId,
			}
		);

		return this.http.post<number>(url, {});
	}
}
